import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "../../contexts/CartContext";

const fmtBR = (n) => Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Floating cart bar — appears above the bottom-nav when the cart has items.
 * Hidden on cart/checkout/tracking routes to avoid stacking.
 */
const HIDDEN_ROUTES = ["/cliente/carrinho", "/cliente/checkout", "/cliente/tracking"];

export default function FloatingCartBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { items, totals, merchant } = useCart();

  if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) return null;
  if (!items.length) return null;

  const minOrder = Number(merchant?.min_order_value || 0);
  const belowMin = minOrder > 0 && totals.subtotal < minOrder;
  const missing = belowMin ? Math.max(0, minOrder - totals.subtotal) : 0;

  return (
    <AnimatePresence>
      <motion.button
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: "spring", stiffness: 360, damping: 30 }}
        onClick={() => navigate("/cliente/carrinho")}
        className={`fixed left-3 right-3 bottom-20 z-30 mx-auto max-w-3xl text-white rounded-2xl shadow-[0_12px_32px_rgba(255,107,0,0.35)] flex items-center justify-between gap-3 pl-3 pr-4 py-3 active:scale-[0.99] transition-transform ${
          belowMin ? "bg-gray-700" : "bg-[#FF6B00]"
        }`}
        data-testid="floating-cart-bar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
            <ShoppingBag className="w-7 h-7" strokeWidth={2.2} />
            <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[11px] font-extrabold flex items-center justify-center shadow-md ${belowMin ? "text-gray-700" : "text-[#FF6B00]"}`} data-testid="floating-cart-count">
              {totals.count}
            </span>
          </div>
          <div className="flex flex-col items-start leading-tight min-w-0">
            <span className="text-[10px] uppercase tracking-wider font-bold opacity-80 truncate max-w-[180px]">
              {belowMin ? `Falta R$ ${fmtBR(missing)} p/ o mínimo` : (merchant?.business_name || "Seu pedido")}
            </span>
            <span className="text-sm font-extrabold tabular-nums" data-testid="floating-cart-total">
              R$ {fmtBR(totals.subtotal)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 font-bold text-sm shrink-0">
          {belowMin ? "Adicionar mais" : "Ver carrinho"}
          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
        </div>
      </motion.button>
    </AnimatePresence>
  );
}
